import { SwitcherStreamSetting } from "@switcherstudio/switcher-api-client";
import { usePageHeader } from "hooks/usePageHeader";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    audioBitRates,
    audioFormats,
    videoBitRates,
    videoFormats
} from "../platforms/platform/custom-rtmp/form-options";
import styles from "./index.module.scss";
import { TextInput } from "components/inputs/text-input/TextInput";
import { Select } from "components/inputs/select/Select";
import { Button } from "components/buttons/Button";
import { useSpeedTest } from "hooks/useSpeedTest";

interface CustomRTMPChannelPageProps {
    btnText: string;
    secondaryBtnText?: string;
    setting: SwitcherStreamSetting;
    onChange: (update: { [key: string]: any }) => void;
    onPrimaryClick: () => void;
    onSecondaryClick?: () => void;
}

export const CustomRTMPChannelPage: React.FC<CustomRTMPChannelPageProps> = ({
    btnText,
    secondaryBtnText,
    setting,
    onChange,
    onPrimaryClick,
    onSecondaryClick
}) => {
    const { t } = useTranslation();

    usePageHeader({
        showBreadcrumbs: true,
        customCrumbs: [
            {
                label: t("page-titles:manage-platforms"),
                route: "/manage-platforms"
            },
            { label: t("page-titles:custom-rtmp"), route: "/" }
        ]
    });

    // allow for new "ID" on audio format
    useEffect(() => {
        if (!setting["audio-channel-id"]) {
            const channelCount = setting["audio-channel-count"];
            const sampleRate = setting["audio-sample-rate"];
            const matchedFormat = audioFormats.find(
                (format) =>
                    format.channelCount === channelCount &&
                    format.sampleRate === sampleRate
            );
            onChange({
                "audio-channel-id": matchedFormat ? matchedFormat.id : "default"
            });
        }
    }, [setting, onChange]);

    const { SpeedTestDisplay } = useSpeedTest();

    return (
        <div className={styles.container}>
            <div className={styles["row"]}>
                <TextInput
                    readonly={!setting["user-editable"]}
                    id="channel-name"
                    label={t("platforms:custom-rtmp-channel-name")}
                    type="text"
                    value={setting["channel-name"]}
                    onChange={(e) =>
                        onChange({ "channel-name": e.target.value })
                    }
                />
            </div>
            <div className={styles["row"]}>
                <TextInput
                    readonly={!setting["user-editable"]}
                    id="server-url"
                    label={t("platforms:custom-rtmp-server-url")}
                    type="text"
                    placeholder="Server URL"
                    value={setting.url}
                    onChange={(e) => onChange({ url: e.target.value })}
                />

                <TextInput
                    readonly={!setting["user-editable"]}
                    id="rtmp-stream"
                    label={t("platforms:custom-rtmp-stream-key")}
                    type="text"
                    placeholder={t("platforms:key-placeholder")}
                    value={setting["rtmp-stream"]}
                    onChange={(e) =>
                        onChange({ "rtmp-stream": e.target.value })
                    }
                />
            </div>
            <fieldset>
                <h6>{t("platforms:custom-rtmp-broadcast-options")}</h6>
                <small>{t("platforms:configure-stream-settings")}</small>
                <div className={styles["speed-test-wrapper"]}>
                    {SpeedTestDisplay}
                </div>
                <div className={styles["row"]}>
                    <Select
                        label={t("platforms:custom-rtmp-video-format")}
                        labelClassName="large-label"
                        id="video-format"
                        selected={setting["video-frame-height"]}
                        onChange={(e) => {
                            const selectedFormat = videoFormats.find(
                                (v) => v.height === parseInt(e.target.value)
                            );
                            onChange({
                                "video-frame-height": selectedFormat.height,
                                "video-frame-width": selectedFormat.width
                            });
                        }}
                        options={videoFormats}
                        optionKey="height"
                        optionLabel="name"
                    />

                    <Select
                        label={t("platforms:custom-rtmp-video-bitrate")}
                        labelClassName="large-label"
                        id="video-bitrate"
                        selected={setting["video-bit-rate"]}
                        onChange={(e) => {
                            onChange({
                                "video-bit-rate": e.target.value
                            });
                        }}
                        options={videoBitRates}
                        optionKey="value"
                        optionLabel="name"
                    />
                </div>
                <div className={`${styles["row"]}`}>
                    <Select
                        label={t("platforms:custom-rtmp-audio-format")}
                        id="audio-format"
                        labelClassName="large-label"
                        selected={setting["audio-channel-id"]}
                        onChange={(e) => {
                            const selectedFormat = audioFormats.find((a) => {
                                return !e.target.value
                                    ? !a.channelCount
                                    : a.id === e.target.value;
                            });
                            onChange({
                                "audio-channel-id": selectedFormat?.id,
                                "audio-channel-count":
                                    selectedFormat?.channelCount,
                                "audio-sample-rate": selectedFormat?.sampleRate
                            });
                        }}
                        options={audioFormats}
                        optionKey="id"
                        optionLabel="name"
                    />
                    <div>
                        <Select
                            label={t("platforms:custom-rtmp-audio-bitrate")}
                            id="audio-bitrate"
                            labelClassName="large-label"
                            selected={setting["audio-bit-rate"]}
                            onChange={(e) => {
                                onChange({
                                    "audio-bit-rate": e.target.value
                                });
                            }}
                            options={audioBitRates}
                            optionKey="value"
                            optionLabel="name"
                        />
                    </div>
                </div>
            </fieldset>
            <div className={styles["button-row"]}>
                <Button onClick={onPrimaryClick}>{btnText}</Button>
                {onSecondaryClick && (
                    <Button onClick={onSecondaryClick} type="danger">
                        {secondaryBtnText}
                    </Button>
                )}
            </div>
        </div>
    );
};
